import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { axiosForAPI } from '../helper_functions/utilities'

const RETRY_WINDOW_MS = 5 * 60 * 1000
const INITIAL_RETRY_DELAY_MS = 1000
const MAX_RETRY_DELAY_MS = 30000

const DeploymentCapabilitiesContext = createContext(null)

function retryDelay(attempt) {
    const exponentialDelay = Math.min(MAX_RETRY_DELAY_MS, INITIAL_RETRY_DELAY_MS * (2 ** attempt))
    return Math.round(exponentialDelay * (0.8 + (Math.random() * 0.4)))
}

export function DeploymentCapabilitiesProvider({ children }) {
    const [status, setStatus] = useState('loading')
    const [capabilities, setCapabilities] = useState(null)
    const [retryToken, setRetryToken] = useState(0)

    useEffect(() => {
        let cancelled = false
        let retryTimer
        const startedAt = Date.now()
        let attempt = 0

        async function loadCapabilities() {
            if (cancelled) return

            try {
                const response = await axiosForAPI.get('/capabilities/', { timeout: 5000 })
                if (cancelled) return
                setCapabilities(response.data)
                setStatus('ready')
            } catch (error) {
                if (cancelled) return

                if (Date.now() - startedAt >= RETRY_WINDOW_MS) {
                    setStatus('failed')
                    return
                }

                setStatus('retrying')
                retryTimer = window.setTimeout(() => {
                    attempt += 1
                    loadCapabilities()
                }, retryDelay(attempt))
            }
        }

        setStatus(retryToken ? 'retrying' : 'loading')
        loadCapabilities()

        return () => {
            cancelled = true
            if (retryTimer) window.clearTimeout(retryTimer)
        }
    }, [retryToken])

    const retry = useCallback(() => {
        setCapabilities(null)
        setRetryToken((token) => token + 1)
    }, [])

    useEffect(() => {
        function retryAfterFocus() {
            if (status === 'failed') retry()
        }

        window.addEventListener('focus', retryAfterFocus)
        return () => window.removeEventListener('focus', retryAfterFocus)
    }, [retry, status])

    const supportedPlatforms = useMemo(
        () => new Set(capabilities?.supportedPlatforms || []),
        [capabilities]
    )

    const value = useMemo(() => ({
        capabilities,
        isReady: status === 'ready',
        isPlatformSupported: (platform) => !platform || (status === 'ready' && supportedPlatforms.has(platform)),
        retry,
        status,
    }), [capabilities, retry, status, supportedPlatforms])

    return (
        <DeploymentCapabilitiesContext.Provider value={value}>
            {children}
        </DeploymentCapabilitiesContext.Provider>
    )
}

export function useDeploymentCapabilities() {
    const context = useContext(DeploymentCapabilitiesContext)
    if (!context) {
        throw new Error('useDeploymentCapabilities must be used inside DeploymentCapabilitiesProvider')
    }
    return context
}

export function scopedPlatformOptions(options, { isReady, isPlatformSupported }) {
    return options.map((option) => {
        if (!option.value) {
            return { ...option, disabled: option.disabled || !isReady }
        }

        const supported = isPlatformSupported(option.value)
        return {
            ...option,
            disabled: !supported,
            label: supported ? option.label : `${option.label} (BETA)`,
            title: supported ? undefined : 'Unavailable during NA beta',
        }
    })
}
